import {
  CompositeInherit,
  TrainingPhraseConfig,
  Attribute,
  ParameterConfig,
  DataQuery,
  ValueSuggestionData,
  ValueAssociation,
  ValueCheck,
  ConfirmationInfo,
  DialogAct,
  OriginKey,
  IntentInfo,
  CompositeInfo,
  EntityInfo,
  ReferenceNode,
  ServiceSlot,
  GenericType,
  ExternalType,
  StateUpdateInfo,
  FrameMapping,
  FrameStorageAnnotation,
  PostFillAction,
  FrameReturn,
  AugmentMeta
} from '@interface/index';
import { ModelDeclare, ModelProxy } from '@lib/model-proxy';
import { effects } from './effect';

// state
export interface StateInfo {
  id: string;
  source?: string;
  originKey?: OriginKey;
  structKey?: OriginKey;
  parameters: ParameterConfig[];
  availableIntents: IntentInfo[];
  availableComposites: CompositeInfo[];
  availableEntities: EntityInfo[];
  availableChannels: string[];
  selectedParameters: Attribute[];
  nestedSlotCustomizations: Attribute[];
  headSlot?: ReferenceNode;
  inheritedParameters: Attribute[];
  trainingPhrases: TrainingPhraseConfig[];
  negativePhrases: TrainingPhraseConfig[];
  dontCareExpressions: TrainingPhraseConfig[];
  parent: any;
  dirty: boolean;
  err: boolean;
  name: string;
  comment?: string;
  showAdvanced: boolean;
  parentId?: string;
  dleStates: string[];
  saveLoading: boolean;
  loading: boolean;
  showParameterEditor: boolean;
  parameterEditorData: any;
  globalParameters: Attribute[];
  scope: string;
  readonly: boolean;
  isFromQuestion: boolean;
  selectedCompositeInherit?: CompositeInherit[];
  weak: boolean;
  isInterface: boolean;
  cuiDisabled?: boolean;
  associateType?: ReferenceNode;
  subOfWeakTypeId?: string;
  useExpression?: ReferenceNode;
  dataQueries: DataQuery[];
  services: ServiceSlot[];
  valueSuggestions: ValueSuggestionData[];
  genericTypes: GenericType[];
  externalTypes: ExternalType[];
  valueAssociations: ValueAssociation[];
  confirmations: ConfirmationInfo[];
  valueChecks: ValueCheck[];
  stateUpdates: StateUpdateInfo[];
  mappings: FrameMapping[];
  dialogActs: DialogAct[];
  postFillActions: PostFillAction[];
  triggers: string[];
  description: string;
  qualification: string;
  externalQualification: boolean;
  sourceType?: string;
  isGlobal: boolean;
  storageEnable?: boolean;
  storageAnnotation?: FrameStorageAnnotation;
  importedFromService?: boolean;
  tab?: string;
  returnInfo?: FrameReturn;
  auxiliary?: boolean;
  booleanGatePrompts?: string[];
  booleanGateExpressionsYes?: TrainingPhraseConfig[];
  booleanGateExpressionsNo?: TrainingPhraseConfig[];
  enableFallback: boolean;
  fallbackInfo?: AugmentMeta;
}

export type PayloadInfo = { [K in keyof StateInfo]: StateInfo[K] };

export const defaultState: PayloadInfo = {
  scope: '',
  id: '',
  parameters: [],
  availableIntents: [],
  availableComposites: [],
  availableEntities: [],
  availableChannels: [],
  selectedParameters: [],
  nestedSlotCustomizations: [],
  trainingPhrases: [],
  negativePhrases: [],
  dontCareExpressions: [],
  inheritedParameters: [],
  dataQueries: [],
  services: [],
  valueSuggestions: [],
  showParameterEditor: false,
  parameterEditorData: [],
  globalParameters: [],
  parent: {},
  readonly: false,
  name: '',
  parentId: '',
  err: false,
  dirty: false,
  weak: false,
  isInterface: false,
  showAdvanced: false,
  dleStates: [],
  saveLoading: false,
  loading: false,
  isFromQuestion: false,
  genericTypes: [],
  externalTypes: [],
  valueAssociations: [],
  confirmations: [],
  valueChecks: [],
  stateUpdates: [],
  mappings: [],
  dialogActs: [],
  triggers: [],
  description: '',
  qualification: '',
  externalQualification: false,
  isGlobal: false,
  postFillActions: [],
  enableFallback: false
};

const composite: ModelDeclare = {
  effects,
  namespace: 'composite',
  state: defaultState,
  actions: {
    clear: () => defaultState
  }
};

export const compositeModel = new ModelProxy<StateInfo, PayloadInfo>(composite);
